import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { softDeleteWorkspace, updateWorkspace } from "@/features/identity/identity.api";
import {
  type UpdateWorkspaceInput,
  updateWorkspaceInputSchema,
} from "@/features/identity/schemas/inputs.schema";
import { SettingsLayout } from "@/features/settings/settings-layout";
import { useAuth } from "@/shared/auth/auth.context";
import { DateField } from "@/shared/components/form/fields/date-field";
import { TextField } from "@/shared/components/form/fields/text-field";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Form } from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";

export function WorkspaceSettingsRoute() {
  const auth = useAuth();
  const qc = useQueryClient();
  const { workspace: activeWs } = useActiveWorkspace();
  const ws = auth.workspaces.find((w) => w.id === activeWs.id);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [typedName, setTypedName] = useState("");

  const form = useForm<UpdateWorkspaceInput>({
    resolver: zodResolver(updateWorkspaceInputSchema),
    defaultValues: {
      name: ws?.name ?? "",
      slug: ws?.slug ?? "",
      operatingDate: ws?.operatingDate ?? "",
    },
  });

  const update = useMutation({
    mutationFn: (changes: { name?: string; slug?: string; operatingDate?: string | null }) =>
      ws ? updateWorkspace(ws.id, changes) : Promise.reject(new Error("no workspace")),
    onSuccess: async (res: { slug: string }) => {
      setSavedNote(res.slug !== ws?.slug ? "Saved. Old URL keeps working for 7 days." : "Saved.");
      await qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });
  const remove = useMutation({
    mutationFn: () =>
      ws ? softDeleteWorkspace(ws.id, typedName) : Promise.reject(new Error("no workspace")),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["auth", "me"] });
      window.location.href = "/";
    },
  });

  useEffect(() => {
    if (!savedNote) return;
    const t = setTimeout(() => setSavedNote(null), 2500);
    return () => clearTimeout(t);
  }, [savedNote]);

  if (!ws) {
    return <div className="px-6 py-8 text-sm text-text-tertiary">No workspace selected.</div>;
  }

  async function onSubmit(values: UpdateWorkspaceInput) {
    setServerError(null);
    setSavedNote(null);
    if (!ws) return;
    const changes: { name?: string; slug?: string; operatingDate?: string | null } = {};
    if (values.name !== ws.name) changes.name = values.name;
    if (values.slug !== ws.slug) changes.slug = values.slug;
    const nextOperatingDate = values.operatingDate || null;
    if (import.meta.env.DEV && nextOperatingDate !== (ws.operatingDate ?? null)) {
      changes.operatingDate = nextOperatingDate;
    }
    if (Object.keys(changes).length === 0) {
      setSavedNote("Nothing to save.");
      return;
    }
    try {
      await update.mutateAsync(changes);
    } catch (e) {
      setServerError(e instanceof Error ? e.message : "Could not save.");
    }
  }

  return (
    <SettingsLayout>
      <div className="flex w-full flex-col gap-6">
        <div>
          <h2 className="font-display text-lg font-semibold tracking-tight">
            Workspace
            <span className="ml-2 font-sans text-sm font-normal text-text-secondary">
              {ws.name}
            </span>
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            Change your workspace name and slug. Changing the slug keeps the old URL working for 7
            days.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">General</CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex flex-col gap-4"
                noValidate
              >
                <TextField control={form.control} name="name" label="Name" />
                <TextField
                  control={form.control}
                  name="slug"
                  label="Slug"
                  hint="Lowercase letters, numbers, dashes. Used in your workspace URL."
                />
                {import.meta.env.DEV ? (
                  <DateField
                    control={form.control}
                    name="operatingDate"
                    label="Workspace today"
                    clearable
                    hint="Leave empty to use the real date. Set it when recording a past or future company day."
                  />
                ) : null}
                {serverError ? <p className="text-sm text-destructive">{serverError}</p> : null}
                {savedNote ? <p className="text-sm text-success-fg">{savedNote}</p> : null}
                <div>
                  <Button type="submit" disabled={form.formState.isSubmitting}>
                    {form.formState.isSubmitting ? "Saving…" : "Save changes"}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>

        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="text-sm font-semibold text-destructive">Danger zone</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-text-secondary">
              Soft-delete this workspace. Account Owner/Admin can restore within 30 days.
            </p>
            <div className="mt-3">
              <Button variant="destructive" onClick={() => setDeleteOpen(true)}>
                Delete workspace…
              </Button>
            </div>
          </CardContent>
        </Card>

        <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete {ws.name}?</DialogTitle>
              <DialogDescription>
                Type the workspace name to confirm. All members lose access immediately. You have 30
                days to restore from Account → Workspaces.
              </DialogDescription>
            </DialogHeader>
            <Input
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder={ws.name}
              autoFocus
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button
                variant="destructive"
                disabled={typedName !== ws.name || remove.isPending}
                onClick={() => remove.mutate()}
              >
                {remove.isPending ? "Deleting…" : "Delete workspace"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SettingsLayout>
  );
}
